import {
  Box,
  Dialog,
  DialogContent,
  Grid,
  IconButton,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { OrganizationUserContext } from "contexts/Organization";
import { SubscriptionContext } from "contexts/Subscription";
import { useContext, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { AddOnItemName, UpgradeDialogSource } from "schemas/dashboard";
import { SetAlertType } from "schemas/functions";
import { LIGHT_GREY } from "styles/oneOffColor";

import StripeCheckout from "components/StripeCheckout";
import SubscriptionButton from "features/Settings/SubscriptionButton";
import { DISPLAYED_ONBOARDING_PAYWALL } from "utils/localStorage";
import { trackEvent } from "utils/tracking";

import CreditsLimitView from "./CreditsLimitView";
import EmailVolumeLimitView from "./EmailVolumeLimitView";
import OnboardingPaywall from "./OnboardingPaywall";
import {
  AI_CREDITS_LIMIT_SOURCES,
  DisplayAllPlans,
  DisplayProAndVa,
  EMAIL_VOLUME_LIMIT_SOURCES,
  YearlyPlanOnly,
  getAllPlans,
} from "./Plan/helpers";
import Subscription from "./Subscription";
import UpgradeFeatureDescription from "./UpgradeFeatureDescription";

interface IProps {
  open: boolean;
  onClose: () => void;
  setAlert: SetAlertType;
}

export function UpgradeDialog({ open, onClose, setAlert }: IProps) {
  const { currentOrg } = useContext(OrganizationUserContext);
  const [searchParams, setSearchParams] = useSearchParams();
  const theme = useTheme();
  const isMobileScreen = useMediaQuery(theme.breakpoints.down("md"));
  const {
    upgradeDialogSource,
    tab,
    openCheckoutDialog,
    setOpenCheckoutDialog,
    subscription,
    addOns,
  } = useContext(SubscriptionContext);

  const close = () => {
    if (openCheckoutDialog) {
      setOpenCheckoutDialog(null);
      searchParams.set("checkout", "failed");
      setSearchParams(searchParams);
    } else {
      setTimeout(() => {
        searchParams.delete("checkout");
        setSearchParams(searchParams);
      }, 500);
    }
    if (upgradeDialogSource === "Onboarding Paywall") {
      trackEvent("Onboarding Paywall - Close Button Clicked");
      localStorage.setItem(
        `${DISPLAYED_ONBOARDING_PAYWALL}-${currentOrg?.id}`,
        "true",
      );
    }

    onClose();
  };

  useEffect(() => {
    if (open) {
      trackEvent("Shown Paywall Dialog", {
        Reason: upgradeDialogSource,
        "Is Mobile Screen": isMobileScreen,
        "Show Autopilot Plan": currentOrg?.showAutopilotPlan,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const getMaxWidth = () => {
    if (openCheckoutDialog?.plan) {
      return "lg";
    }
    if (isAiCreditsLimit) {
      return "xs";
    }
    const totalPlansDisplayed = getAllPlans(tab, upgradeDialogSource)?.length;
    if (totalPlansDisplayed === 1) return "sm";
    if (totalPlansDisplayed === 2 && !currentOrg?.showAutopilotPlan)
      return "md";
    return "lg";
  };

  const isEmailVolumeLimit = Boolean(
    upgradeDialogSource &&
      EMAIL_VOLUME_LIMIT_SOURCES.includes(upgradeDialogSource) &&
      subscription?.isSubscribed,
  );

  const isAiCreditsLimit = Boolean(
    upgradeDialogSource &&
      AI_CREDITS_LIMIT_SOURCES.includes(upgradeDialogSource) &&
      subscription?.isSubscribed &&
      addOns?.[AddOnItemName.ai_credits_750],
  );

  const renderDialog = () => {
    if (!openCheckoutDialog?.plan) {
      if (upgradeDialogSource === "Onboarding Paywall") {
        return <OnboardingPaywall />;
      }
      return (
        <>
          {searchParams.get("checkout") !== "failed" && (
            <>
              <Grid container justifyContent="flex-end" sx={{ pr: 2, pt: 2 }}>
                <IconButton
                  aria-label="close"
                  onClick={close}
                  sx={{
                    width: 30,
                    height: 30,
                    color: (theme) => theme.palette.grey[500],
                  }}
                >
                  <Box component="i" className="fa-thin fa-xmark" />
                </IconButton>
              </Grid>
              <DialogContent sx={isAiCreditsLimit ? { pt: 0 } : undefined}>
                {isEmailVolumeLimit ? (
                  <EmailVolumeLimitView />
                ) : isAiCreditsLimit ? (
                  <CreditsLimitView />
                ) : (
                  <>
                    <Typography
                      sx={{
                        fontSize: { xs: 20, md: 26 },
                      }}
                      textAlign={"center"}
                    >
                      {!upgradeDialogSource ||
                      DisplayAllPlans.includes(upgradeDialogSource) ||
                      DisplayProAndVa.includes(upgradeDialogSource) ||
                      [
                        UpgradeDialogSource.gamification_more_emails,
                        UpgradeDialogSource.instagram_onboarding_paywall_pro,
                        UpgradeDialogSource.template_evaluation_limit,
                      ]?.includes(upgradeDialogSource)
                        ? "Pick Your Subscription Plan"
                        : "This is a feature for Paid Users 🔐"}
                    </Typography>

                    {upgradeDialogSource && (
                      <UpgradeFeatureDescription
                        upgradeDialogSource={upgradeDialogSource}
                      />
                    )}

                    {!(
                      isMobileScreen ||
                      (upgradeDialogSource &&
                        YearlyPlanOnly?.includes(upgradeDialogSource))
                    ) && (
                      <Grid sx={{ mb: 3 }} container justifyContent="center">
                        <SubscriptionButton />
                      </Grid>
                    )}
                    <Subscription upgradeDialogSource={upgradeDialogSource} />
                  </>
                )}
              </DialogContent>
            </>
          )}
        </>
      );
    } else {
      return <StripeCheckout openCheckoutDialog={openCheckoutDialog} />;
    }
  };

  return (
    <Dialog
      open={open}
      onClose={close}
      maxWidth={getMaxWidth()}
      fullScreen={isMobileScreen}
      fullWidth
      sx={{
        "& .MuiDialog-paper": {
          borderRadius: { xs: 0, md: 4 },
          backgroundColor: isMobileScreen ? LIGHT_GREY : "",
        },
      }}
    >
      {renderDialog()}
    </Dialog>
  );
}
